import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { adminUsersApi } from "@/lib/api/adminUsers";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

const ROLES = ["OPS_ADMIN", "FINANCE_ADMIN", "SALES_AGENT"];

const EMPTY = {
    full_name: "",
    email: "",
    username: "",
    phone: "",
    role: "OPS_ADMIN",
    password: "",
    referral_code: "",
    is_active: true,
};

export default function UserFormDialog({ open, onOpenChange, initial, onSaved }) {
    const { t, isRTL } = useI18n();
    const [form, setForm] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        if (initial?.id) {
            setForm({
                full_name: initial.full_name || "",
                email: initial.email || "",
                username: initial.username || "",
                phone: initial.phone || "",
                role: initial.role || "OPS_ADMIN",
                password: "",
                referral_code: initial.referral_code || "",
                is_active: !!initial.is_active,
            });
        } else {
            setForm(EMPTY);
        }
        setError("");
    }, [open, initial]);

    const update = (patch) => setForm((p) => ({ ...p, ...patch }));

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setSubmitting(true);
        try {
            let saved;
            if (initial?.id) {
                const payload = {
                    full_name: form.full_name.trim(),
                    role: form.role,
                    is_active: form.is_active,
                };
                if (form.username.trim()) payload.username = form.username.trim().toLowerCase();
                if (form.phone.trim()) payload.phone = form.phone.trim();
                if (form.password.trim()) payload.password = form.password.trim();
                if (form.role === "SALES_AGENT" && form.referral_code.trim()) {
                    payload.referral_code = form.referral_code.trim().toUpperCase();
                }
                saved = await adminUsersApi.update(initial.id, payload);
                toast.success(t("admin.users.updated"));
            } else {
                if (!form.password.trim()) throw new Error(t("admin.users.errors.passwordRequired"));
                if (!form.username.trim()) throw new Error(t("admin.users.errors.usernameRequired"));
                const payload = {
                    full_name: form.full_name.trim(),
                    email: form.email.trim().toLowerCase(),
                    username: form.username.trim().toLowerCase(),
                    role: form.role,
                    password: form.password.trim(),
                };
                if (form.phone.trim()) payload.phone = form.phone.trim();
                if (form.role === "SALES_AGENT" && form.referral_code.trim()) {
                    payload.referral_code = form.referral_code.trim().toUpperCase();
                }
                saved = await adminUsersApi.create(payload);
                toast.success(t("admin.users.created"));
            }
            onSaved?.(saved);
            onOpenChange(false);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const isSales = form.role === "SALES_AGENT";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-lg rounded-2xl"
                data-testid="user-dialog"
                dir={isRTL ? "rtl" : "ltr"}
            >
                <DialogHeader>
                    <DialogTitle>{t(initial?.id ? "admin.users.editTitle" : "admin.users.createTitle")}</DialogTitle>
                    <DialogDescription>{t("admin.users.dialogDescription")}</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2">
                    {/* Full name */}
                    <div>
                        <Label htmlFor="u_name">{t("admin.users.fields.fullName")}</Label>
                        <Input
                            id="u_name" required
                            value={form.full_name}
                            onChange={(e) => update({ full_name: e.target.value })}
                            className="mt-1.5"
                            data-testid="user-full-name"
                        />
                    </div>

                    {/* Email — locked after creation */}
                    <div>
                        <Label htmlFor="u_email">{t("admin.users.fields.email")}</Label>
                        <Input
                            id="u_email" type="email" dir="ltr" required
                            value={form.email}
                            onChange={(e) => update({ email: e.target.value })}
                            disabled={!!initial?.id}
                            className="mt-1.5"
                            data-testid="user-email"
                        />
                        {initial?.id && (
                            <p className="mt-1 text-xs text-muted-foreground">{t("admin.users.fields.emailLocked")}</p>
                        )}
                    </div>

                    {/* Username */}
                    <div>
                        <Label htmlFor="u_username">{t("admin.users.fields.username")}</Label>
                        <Input
                            id="u_username" dir="ltr" required={!initial?.id}
                            placeholder={t("admin.users.fields.usernamePlaceholder")}
                            value={form.username}
                            onChange={(e) => update({ username: e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, "") })}
                            className="mt-1.5"
                            data-testid="user-username"
                        />
                        <p className="mt-1 text-xs text-muted-foreground">{t("admin.users.fields.usernameHint")}</p>
                    </div>

                    {/* Phone */}
                    <div>
                        <Label htmlFor="u_phone">
                            {t("admin.users.fields.phone")}
                            <span className="ms-1 text-xs text-muted-foreground">({t("common.optional")})</span>
                        </Label>
                        <Input
                            id="u_phone" type="tel" dir="ltr"
                            placeholder={t("admin.users.fields.phonePlaceholder")}
                            value={form.phone}
                            onChange={(e) => update({ phone: e.target.value })}
                            className="mt-1.5"
                            data-testid="user-phone"
                        />
                    </div>

                    {/* Role */}
                    <div>
                        <Label>{t("admin.users.fields.role")}</Label>
                        <Select value={form.role} onValueChange={(v) => update({ role: v })}>
                            <SelectTrigger className="mt-1.5" data-testid="user-role">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ROLES.map((r) => (
                                    <SelectItem key={r} value={r}>{t(`roles.${r}`)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Referral code — only for Sales Agent */}
                    {isSales && (
                        <div>
                            <Label htmlFor="u_ref">
                                {t("admin.users.fields.referralCode")}
                                <span className="ms-1 text-xs text-muted-foreground">({t("common.optional")})</span>
                            </Label>
                            <Input
                                id="u_ref" dir="ltr"
                                placeholder={form.username
                                    ? form.username.toUpperCase()
                                    : t("admin.users.fields.referralCodePlaceholder")}
                                value={form.referral_code}
                                onChange={(e) => update({ referral_code: e.target.value.toUpperCase().replace(/[^A-Z0-9_.-]/g, "") })}
                                className="mt-1.5"
                                data-testid="user-referral-code"
                            />
                            <p className="mt-1 text-xs text-muted-foreground">{t("admin.users.fields.referralCodeHint")}</p>
                        </div>
                    )}

                    {/* Password */}
                    <div>
                        <Label htmlFor="u_pwd">{t(initial?.id ? "admin.users.fields.passwordReset" : "admin.users.fields.password")}</Label>
                        <Input
                            id="u_pwd" type="text" dir="ltr"
                            placeholder={initial?.id ? t("admin.users.fields.passwordResetPlaceholder") : ""}
                            value={form.password}
                            onChange={(e) => update({ password: e.target.value })}
                            className="mt-1.5"
                            data-testid="user-password"
                        />
                        <p className="mt-1 text-xs text-muted-foreground">{t("admin.users.fields.passwordHint")}</p>
                    </div>

                    {/* Active toggle — edit only */}
                    {initial?.id && (
                        <div className="flex items-center justify-between rounded-xl border border-border p-3">
                            <div>
                                <Label className="cursor-pointer">{t("admin.users.fields.isActive")}</Label>
                                <p className="text-xs text-muted-foreground mt-0.5">{t("admin.users.fields.isActiveHint")}</p>
                            </div>
                            <Switch
                                checked={form.is_active}
                                onCheckedChange={(v) => update({ is_active: v })}
                                data-testid="user-is-active"
                            />
                        </div>
                    )}

                    {error && (
                        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive" data-testid="user-form-error">
                            {error}
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="user-cancel">
                            {t("common.cancel")}
                        </Button>
                        <Button type="submit" disabled={submitting} data-testid="user-save">
                            {submitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                            {t("common.save")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
